import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FeedbackCategory } from '../../services/feedback.service';

/**
 * Data passed to the feedback dialog when opened.
 */
export interface FeedbackDialogData {
  /** Initial category for an error-triggered report. */
  initialCategory?: FeedbackCategory;
  /** Editable description prefilled from the user-visible error. */
  initialDescription?: string;
  /** Human-readable list of allowlisted diagnostics that will be included. */
  diagnosticItems?: string[];
  /** Pre-populated chat log JSON (if opened from chat context) */
  chatLogJson?: string;
  /** Chat session ID */
  chatSessionId?: string;
  /** Current page URL */
  pageUrl?: string;
  /** UI state snapshot as JSON */
  uiStateJson?: string;
}

/**
 * Result returned when the user submits the feedback dialog.
 * The caller is responsible for performing the actual submission.
 */
export interface FeedbackDialogResult {
  description: string;
  category: FeedbackCategory;
  includeChatLog: boolean;
  userConsent: boolean;
}

/**
 * Feedback dialog component for in-app issue reporting (Issue #577).
 *
 * Collects user input and returns a {@link FeedbackDialogResult} on submit.
 * The actual backend call is handled by the caller (ChatComponent) so that
 * the dialog closes immediately and submission runs in the background
 * (Issue #950).
 */
@Component({
  selector: 'app-feedback-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatIconModule,
    MatTooltipModule,
  ],
  templateUrl: './feedback-dialog.component.html',
  styleUrls: ['./feedback-dialog.component.scss'],
})
export class FeedbackDialogComponent {
  description = '';
  category: FeedbackCategory = 'BUG';
  includeChatLog = false;
  userConsent = false;

  readonly categories: { value: FeedbackCategory; label: string; icon: string }[] = [
    { value: 'BUG', label: 'Bug Report', icon: 'bug_report' },
    { value: 'FEATURE_REQUEST', label: 'Feature Request', icon: 'lightbulb' },
    { value: 'QUESTION', label: 'Question', icon: 'help' },
    { value: 'OTHER', label: 'Other', icon: 'feedback' },
  ];

  readonly dialogRef = inject(MatDialogRef<FeedbackDialogComponent>);
  readonly data: FeedbackDialogData = inject(MAT_DIALOG_DATA);

  constructor() {
    this.category = this.data?.initialCategory ?? 'BUG';
    this.description = this.data?.initialDescription ?? '';
    if (this.data?.chatLogJson) {
      this.includeChatLog = true;
    }
  }

  get isSubmitDisabled(): boolean {
    return !this.description.trim() || !this.userConsent;
  }

  get submitTooltip(): string {
    const missing: string[] = [];
    if (!this.description.trim()) missing.push('a problem description');
    if (!this.userConsent) missing.push('consent to include your email');
    return missing.length > 0 ? `Please provide ${missing.join(' and ')}` : '';
  }

  submit() {
    if (!this.description.trim() || !this.userConsent) return;

    const result: FeedbackDialogResult = {
      description: this.description.trim(),
      category: this.category,
      includeChatLog: this.includeChatLog,
      userConsent: this.userConsent,
    };
    this.dialogRef.close(result);
  }
}
